import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { createReceiptFile, deleteReceiptFile } from "api/receipt";
import { ConfirmDialog, Spacer } from "components/Custom";
import { useLayoutDispatch, popupNotification } from "context/LayoutContext";
import { toBase64Handler } from "utils/base64";
import { timezoneFormat } from "utils/date";
import { sliceText } from "utils/string";

import {
  Calendar,
  Close,
  ExpandMore,
  ExternalLink,
  Trash,
} from "icons";

import useStyles from "./drawerStyle";

let intervalID;

const DeleteFileModal = ({ file_id, onClose, onDelete }) => {
  const layoutDispatch = useLayoutDispatch();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <ConfirmDialog
      open={isLoading || !!file_id}
      title="Delete File"
      children="Receipt file will be deleted?"
      confirmText="Delete"
      onConfirm={() => {
        setIsLoading(true);
        deleteReceiptFile({
          id: file_id,
          responseSetter: (res) => {
            setIsLoading(false);
            if (res.success) {
              onClose(true);
              onDelete();
              popupNotification({
                dispatch: layoutDispatch,
                status: "success",
                message: `Receipt file deleted.`,
              });
            } else {
              if (res.message) {
                popupNotification({
                  dispatch: layoutDispatch,
                  status: "error",
                  message: res.message,
                });
              }
            }
          },
        });
      }}
      isLoading={isLoading}
      onClose={() => onClose(false)}
    />
  );
};

export default function ReceiptDrawer({ open, onClose, receipt, onFileDelete }) {
  const classes = useStyles();

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      PaperProps={{
        className: classes.drawerPaper,
      }}
    >
      <div className={classes.root}>
        <div className={classes.title}>
          <div className={classes.header}>
            <Typography variant="span">{receipt.number}</Typography>
            {receipt.date && (
              <Grid container alignItems="center" justifyContent="center">
                <Grid item className={classes.calendarIcon}>
                  <Calendar size={0.7} color="white" />
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="white">
                    {receipt.date}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </div>
          <IconButton
            disableRipple={true}
            onClick={() => onClose(false)}
            className={classes.orange}
          >
            <Close color="#FFF" />
          </IconButton>
        </div>
        <FilesAccordion 
          receipt={receipt} 
          onClose={onClose} 
          onFileDelete={onFileDelete} 
        />
      </div>
    </Drawer>
  );
}

function FilesAccordion({ receipt, onClose, onFileDelete }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const layoutDispatch = useLayoutDispatch();
  const [deleteId, setDeleteId] = useState(null);

  const handleFileChange = ({ target: { files } }) => {
    const uploaded = [...uploadedFiles];
    const filesArray = [...files];
    filesArray
      .filter(
        (file) => uploadedFiles.findIndex((f) => f.name === file.name) === -1,
      )
      .map((file) => {
        uploaded.push(file);
        return file;
      });
    setUploadedFiles(filesArray);
  };

  const handleSubmit = async () => {
    const files = await toBase64Handler(uploadedFiles);
    const fileNames = uploadedFiles.map((file) => file.name);
    const fileTypes = [];
    fileNames.forEach((name) => {
      if (name.toLowerCase().includes(".pdf")) {
        fileTypes.push("PDF");
      } else {
        fileTypes.push("EXCEL");
      }
    });
    setIsLoading(true);
    createReceiptFile({
      body: {
        files,
        fileNames,
        fileTypes,
        receiptId: receipt.id,
      },
      responseSetter: (res) => {
        if (res.success) {
          intervalID = setInterval(() => {
            createReceiptFile({
              body: {
                key: res.key,
              },
              responseSetter: (res) => {
                if (!res.success && res.message) {
                  setIsLoading(false);
                  popupNotification({
                    dispatch: layoutDispatch,
                    status: "error",
                    message: res.message,
                  });
                  return clearInterval(intervalID);
                } else if (res.success) {
                  setIsLoading(false);
                  onClose(true);
                  setUploadedFiles([]);
                  popupNotification({
                    dispatch: layoutDispatch,
                    status: "success",
                    message: `Receipt file uploaded.`,
                  });
                  return clearInterval(intervalID);
                }
              },
            });
          }, 5000);
        } else {
          if (res.message) {
            popupNotification({
              dispatch: layoutDispatch,
              status: "error",
              message: res.message,
            });
          }
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      className={classes.accordion}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
      >
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography className={classes.heading}>Attachments</Typography>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {receipt.files?.map((file) => (
            <Grid item xs={12} className={classes.fileContainer} key={file.id}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Tooltip
                    title={`${file.file_name} - ${timezoneFormat(
                      file.created_at,
                    ).slice(0, 8)}`}
                    aria-label="hello"
                  >
                    <Typography variant="body2" color="textPrimary">
                      {file.file_name.slice(0, 26)}
                      {file.file_name.length > 26
                        ? `...${file.file_name.split(".")[1].toUpperCase()}`
                        : ""}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setDeleteId(file.id)}
                      >
                        <Trash color="red" size={0.8} />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => window.open(file.signed_url, "_blank")}
                      >
                        <ExternalLink size={0.8} color="green" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}
          {receipt.files?.length > 0 && <Spacer height="1rem" />}
          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <label htmlFor="file-upload">
                  <input
                    id="file-upload"
                    type="file"
                    accept="application/pdf, .xlsx, .xls"
                    onChange={handleFileChange}
                    multiple
                    hidden
                  />
                  <Button
                    variant="contained"
                    size="medium"
                    color="primary"
                    component="span"
                    disabled={isLoading}
                  >
                    {uploadedFiles.length
                      ? uploadedFiles.length > 1
                        ? uploadedFiles.length + " Files"
                        : sliceText(uploadedFiles[0].name, 25)
                      : "Select File"}
                  </Button>
                </label>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={uploadedFiles.length === 0 || isLoading}
                  onClick={handleSubmit}
                >
                  {isLoading ? <CircularProgress size={20} /> : "Upload"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
      <DeleteFileModal
        file_id={deleteId}
        onClose={() => setDeleteId(null)}
        onDelete={onFileDelete}
      />
    </Accordion>
  );
}
